import { useEffect, RefObject, SetStateAction, Dispatch } from 'react';

import { OptionGroupProps, SelectOption, SelectValue } from '../Select';
import { KEYS, KEY_VALUES } from '../helpers/constants';
import { isOptionGroup } from '../helpers/helpers';

export type UseKeyboardControl<Option extends SelectOption> = {
  setScrollTop: Dispatch<SetStateAction<number>>;
  hoveredOption: Option | null;
  options: (Option | OptionGroupProps)[];
  onSelect: (value: SelectValue, option: Option, shouldClose?: boolean) => void;
  setHoveredOption: (option: Option) => void;
  setShouldUseMouseHover: (value: boolean) => void;
  hasTopPlacement: boolean;
  optionsRefs: RefObject<{ [key: string]: HTMLDivElement | null }>;
  contentRef: RefObject<HTMLDivElement>;
  isOverlayOpen: boolean;
  isMultiple?: boolean;
  isVirtualized?: boolean;
  virtualizationRowHeight?: number;
};

const prepareIndex = (index: number, length: number) => {
  return (length + index) % length;
};

const useKeyboardControl = <Option extends SelectOption>({
  setScrollTop,
  hoveredOption,
  options,
  setHoveredOption,
  setShouldUseMouseHover,
  onSelect,
  hasTopPlacement,
  isOverlayOpen,
  optionsRefs,
  contentRef,
  isMultiple = false,
  isVirtualized = false,
  virtualizationRowHeight = 0,
}: UseKeyboardControl<Option>) => {
  useEffect(() => {
    if (!isOverlayOpen || options.length === 0) {
      return;
    }

    const callback = (event: KeyboardEvent) => {
      const key = event.key || String(event.keyCode);

      switch (KEY_VALUES[key]) {
        case KEYS.ARROW_UP:
        case KEYS.ARROW_DOWN: {
          if (event.shiftKey || event.metaKey) {
            return;
          }

          event.preventDefault();
          setShouldUseMouseHover(false);

          let indexStep = key === KEYS.ARROW_UP ? -1 : 1;
          let nextIndex = 0;

          if (hasTopPlacement) {
            indexStep = -indexStep;
          }

          if (hoveredOption !== null) {
            nextIndex = options.findIndex(
              (option) => !isOptionGroup(option) && option.value === hoveredOption.value
            );

            if (nextIndex === -1) {
              nextIndex = 0;
            }
          }

          if (hoveredOption === null && indexStep === 1) {
            nextIndex = prepareIndex(nextIndex, options.length);
          } else {
            nextIndex = prepareIndex(nextIndex + indexStep, options.length);
          }

          if (options.some((option) => !isOptionGroup(option) && !option.isDisabled)) {
            let nextOption = options[nextIndex];

            while (isOptionGroup(nextOption) || nextOption.isDisabled) {
              nextIndex = prepareIndex(nextIndex + indexStep, options.length);
              nextOption = options[nextIndex];
            }
          } else {
            return;
          }

          const nextValue = (options[nextIndex] as SelectOption).value;

          setHoveredOption(options[nextIndex] as Option);

          if (!contentRef.current || !optionsRefs.current![nextValue]) {
            // Чтобы обрабатывать в виртуализированном листе переходы с первого до последнего и наоборот
            if (isVirtualized) {
              setScrollTop(nextIndex * virtualizationRowHeight);
            }

            return;
          }

          const contentBoundingRect = contentRef.current.getBoundingClientRect();
          const optionBoundingRect = optionsRefs.current![nextValue]!.getBoundingClientRect();

          const optionOffsetFromOverlay = optionBoundingRect.top - contentBoundingRect.top;

          if (optionOffsetFromOverlay + optionBoundingRect.height > contentBoundingRect.height) {
            const scrollTop =
              optionOffsetFromOverlay + optionBoundingRect.height - contentBoundingRect.height;

            contentRef.current.scrollTop += scrollTop;
            setScrollTop((prev) => prev + scrollTop);
          } else if (optionOffsetFromOverlay < 0) {
            contentRef.current.scrollTop += optionOffsetFromOverlay;
            setScrollTop((prev) => prev + optionOffsetFromOverlay);
          }

          break;
        }
        case KEYS.TAB:
        case KEYS.ENTER: {
          if (hoveredOption !== null) {
            event.stopPropagation();
            event.preventDefault();

            onSelect(hoveredOption.value, hoveredOption);
          }

          break;
        }
        default:
          break;
      }
    };

    document.addEventListener('keydown', callback, true);
    return () => {
      document.removeEventListener('keydown', callback, true);
    };
  }, [
    hoveredOption,
    options,
    onSelect,
    hasTopPlacement,
    isOverlayOpen,
    optionsRefs,
    contentRef,
    isMultiple,
  ]);
};

export default useKeyboardControl;
